import { useQuery } from "@tanstack/react-query";
import { type ReactElement } from "react";

import { HubPdfDocumentView } from "@/components/HubPdfDocumentView";
import { hubPreviewFrameClassName } from "@/lib/hubLayoutClassNames";
import { inferHubFilePreviewKind } from "@/lib/hubFilePreview";
import { cn } from "@/lib/utils";

const MAX_TEXT_CHARS = 400_000;

export function HubBlobUrlPreview({
  previewUrl,
  filename,
  contentType,
  fillAvailableHeight = false,
}: {
  previewUrl: string;
  filename: string;
  contentType?: string | null;
  fillAvailableHeight?: boolean;
  hidePdfSidebar?: boolean;
}): ReactElement {
  const kind = inferHubFilePreviewKind(filename, contentType);

  const textQuery = useQuery({
    queryKey: ["hub-blob-preview-text", previewUrl],
    queryFn: async () => {
      const response = await fetch(previewUrl);
      if (!response.ok) {
        throw new Error(`Could not load preview (${response.status}).`);
      }
      const text = await response.text();
      const truncated = text.length > MAX_TEXT_CHARS;
      return { text: truncated ? text.slice(0, MAX_TEXT_CHARS) : text, truncated };
    },
    enabled: kind === "text",
    staleTime: 120_000,
    gcTime: 120_000,
  });

  const frameClass = hubPreviewFrameClassName(fillAvailableHeight ? "fill" : "inline");

  if (kind === "pdf") {
    return (
      <HubPdfDocumentView
        url={previewUrl}
        title={`PDF preview of ${filename}`}
        className={frameClass}
      />
    );
  }

  if (kind === "image") {
    return (
      <div
        className={cn(
          "flex items-center justify-center overflow-auto",
          fillAvailableHeight && "min-h-0 flex-1",
        )}
      >
        <img
          src={previewUrl}
          alt=""
          className={
            fillAvailableHeight
              ? "max-h-full max-w-full flex-1 object-contain"
              : "max-h-[min(80vh,900px)] max-w-full object-contain"
          }
        />
      </div>
    );
  }

  if (kind === "text") {
    if (textQuery.isPending) {
      return <p className="py-12 text-center text-sm text-slate-500">Loading preview…</p>;
    }
    if (textQuery.isError) {
      return (
        <p className="px-3 py-8 text-center text-sm text-red-700">
          {(textQuery.error as Error).message ?? "Could not load preview."}
        </p>
      );
    }
    const payload = textQuery.data;
    return (
      <div className={cn("overflow-auto rounded-md border border-slate-200 bg-white", frameClass)}>
        <pre className="whitespace-pre-wrap break-words px-3 py-2 font-mono text-xs leading-relaxed text-slate-800">
          {payload.text}
        </pre>
        {payload.truncated ? (
          <p className="border-t border-slate-200 px-3 py-2 text-xs text-slate-500">
            Showing the first {MAX_TEXT_CHARS.toLocaleString()} characters only.
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <p className="py-10 text-center text-sm text-slate-600">
      No inline preview for this file type. Use download to open the file locally.
    </p>
  );
}
